<template>
  <ManagerLayout>
    <div class="manager-processing">
      <h2 class="page-title">処理エラー・キュー状況</h2>
      <p class="page-desc">音声処理キューと失敗・再試行の状況を確認し、不具合の早期発見に役立てます。</p>

      <!-- サマリ -->
      <section class="manager-section summary-section">
        <h3 class="section-title">サマリ</h3>
        <div v-if="summaryLoading" class="summary-loading">読み込み中...</div>
        <div v-else class="summary-cards">
          <div class="summary-group">
            <h4 class="summary-group-title">キュー</h4>
            <div class="summary-row">
              <div class="summary-card">
                <span class="summary-label">待機</span>
                <span class="summary-value">{{ summary.queue.pending }}</span>
                <span class="summary-unit">件</span>
              </div>
              <div class="summary-card">
                <span class="summary-label">処理中</span>
                <span class="summary-value">{{ summary.queue.processing }}</span>
                <span class="summary-unit">件</span>
              </div>
              <div class="summary-card">
                <span class="summary-label">完了</span>
                <span class="summary-value">{{ summary.queue.completed }}</span>
                <span class="summary-unit">件</span>
              </div>
              <div class="summary-card failed">
                <span class="summary-label">失敗</span>
                <span class="summary-value">{{ summary.queue.failed }}</span>
                <span class="summary-unit">件</span>
              </div>
            </div>
          </div>
          <div class="summary-group">
            <h4 class="summary-group-title">失敗・再試行</h4>
            <div class="summary-row">
              <div class="summary-card">
                <span class="summary-label">再試行待ち</span>
                <span class="summary-value">{{ summary.failures.pendingRetry }}</span>
                <span class="summary-unit">件</span>
              </div>
              <div class="summary-card">
                <span class="summary-label">再試行中</span>
                <span class="summary-value">{{ summary.failures.retrying }}</span>
                <span class="summary-unit">件</span>
              </div>
              <div class="summary-card danger">
                <span class="summary-label">恒久失敗</span>
                <span class="summary-value">{{ summary.failures.permanentFailure }}</span>
                <span class="summary-unit">件</span>
              </div>
              <div class="summary-card">
                <span class="summary-label">再試行完了</span>
                <span class="summary-value">{{ summary.failures.completed }}</span>
                <span class="summary-unit">件</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- キュー一覧 -->
      <section class="manager-section">
        <div class="card">
          <h3 class="section-title">キュー一覧</h3>
          <div class="filters">
            <label class="filter-item">
              <span class="filter-label">ステータス</span>
              <select v-model="queueStatus" class="form-select" @change="loadQueue">
                <option value="all">すべて</option>
                <option value="pending">待機</option>
                <option value="processing">処理中</option>
                <option value="completed">完了</option>
                <option value="failed">失敗のみ</option>
              </select>
            </label>
            <button type="button" class="btn btn-primary" :disabled="queueLoading" @click="loadQueue">
              {{ queueLoading ? '読み込み中...' : '検索' }}
            </button>
          </div>
          <div class="toolbar">
            <span class="total-note">全 {{ queueTotal }} 件</span>
            <button type="button" class="btn btn-secondary btn-sm" :disabled="queueLoading" @click="loadQueue">更新</button>
          </div>
          <div v-if="queueLoading" class="loading-state">読み込み中...</div>
          <div v-else-if="!queueItems.length" class="empty-state">該当するキューがありません</div>
          <div v-else class="table-wrap">
            <table class="data-table" aria-label="キュー一覧">
              <thead>
                <tr>
                  <th>録音ID</th>
                  <th>Day</th>
                  <th>種別</th>
                  <th>コーデック</th>
                  <th>ステータス</th>
                  <th>エラー</th>
                  <th>登録日時（JST）</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="r in queueItems" :key="r.recordingId" class="data-row">
                  <td><code class="id-cell">{{ r.recordingId }}</code></td>
                  <td>{{ r.day }}</td>
                  <td>{{ r.typeLabel }}</td>
                  <td>{{ r.codecType }}</td>
                  <td><span class="status-badge" :class="r.status">{{ r.statusLabel }}</span></td>
                  <td class="error-cell">{{ r.errorMessage || '—' }}</td>
                  <td>{{ formatDateTimeJst(r.createdAt) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="queueItems.length > 0 && queueItems.length < queueTotal" class="load-more">
            <button type="button" class="btn btn-secondary" :disabled="queueLoadingMore" @click="loadMoreQueue">
              {{ queueLoadingMore ? '読み込み中...' : 'さらに読み込む' }}
            </button>
          </div>
        </div>
      </section>

      <!-- 失敗一覧 -->
      <section class="manager-section">
        <div class="card">
          <h3 class="section-title">失敗・再試行一覧</h3>
          <div class="filters">
            <label class="filter-item">
              <span class="filter-label">ステータス</span>
              <select v-model="failuresStatus" class="form-select" @change="loadFailures">
                <option value="all">すべて</option>
                <option value="pending_retry">再試行待ち</option>
                <option value="retrying">再試行中</option>
                <option value="permanent_failure">恒久失敗のみ</option>
                <option value="completed">再試行完了</option>
              </select>
            </label>
            <button type="button" class="btn btn-primary" :disabled="failuresLoading" @click="loadFailures">
              {{ failuresLoading ? '読み込み中...' : '検索' }}
            </button>
          </div>
          <div class="toolbar">
            <span class="total-note">全 {{ failuresTotal }} 件</span>
            <button type="button" class="btn btn-secondary btn-sm" :disabled="failuresLoading" @click="loadFailures">更新</button>
          </div>
          <div v-if="failuresLoading" class="loading-state">読み込み中...</div>
          <div v-else-if="!failuresItems.length" class="empty-state">該当する失敗記録がありません</div>
          <div v-else class="table-wrap">
            <table class="data-table" aria-label="失敗一覧">
              <thead>
                <tr>
                  <th>録音ID</th>
                  <th>Day</th>
                  <th>種別</th>
                  <th>再試行回数</th>
                  <th>ステータス</th>
                  <th>エラー</th>
                  <th>失敗日時（JST）</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="r in failuresItems" :key="r.recordingId" class="data-row">
                  <td><code class="id-cell">{{ r.recordingId }}</code></td>
                  <td>{{ r.day }}</td>
                  <td>{{ r.typeLabel }}</td>
                  <td>{{ r.retryCount }}</td>
                  <td><span class="status-badge" :class="r.status">{{ r.statusLabel }}</span></td>
                  <td class="error-cell">{{ r.errorMessage || '—' }}</td>
                  <td>{{ formatDateTimeJst(r.failedAt) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="failuresItems.length > 0 && failuresItems.length < failuresTotal" class="load-more">
            <button type="button" class="btn btn-secondary" :disabled="failuresLoadingMore" @click="loadMoreFailures">
              {{ failuresLoadingMore ? '読み込み中...' : 'さらに読み込む' }}
            </button>
          </div>
        </div>
      </section>
    </div>
  </ManagerLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import ManagerLayout from '../../components/layout/ManagerLayout.vue'
import { supabase } from '../../supabaseClient'

const apiBase = import.meta.env.VITE_API_BASE_URL || ''
const PAGE_SIZE = 100

const summary = ref({
  queue: { pending: 0, processing: 0, completed: 0, failed: 0 },
  failures: { pendingRetry: 0, retrying: 0, permanentFailure: 0, completed: 0 }
})
const summaryLoading = ref(false)

const queueStatus = ref('all')
const queueItems = ref([])
const queueTotal = ref(0)
const queueLoading = ref(false)
const queueLoadingMore = ref(false)
const queueOffset = ref(0)

const failuresStatus = ref('all')
const failuresItems = ref([])
const failuresTotal = ref(0)
const failuresLoading = ref(false)
const failuresLoadingMore = ref(false)
const failuresOffset = ref(0)

const getToken = async () => {
  const { data: { session } } = await supabase.auth.getSession()
  return session?.access_token
}

function formatDateTimeJst (iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', dateStyle: 'short', timeStyle: 'short' })
}

const loadSummary = async () => {
  summaryLoading.value = true
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/processing-summary`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await res.json().catch(() => ({}))
    if (res.ok && data) {
      summary.value = {
        queue: data.queue || summary.value.queue,
        failures: data.failures || summary.value.failures
      }
    }
  } finally {
    summaryLoading.value = false
  }
}

function queueParams (offset = 0) {
  const params = new URLSearchParams()
  if (queueStatus.value !== 'all') params.set('status', queueStatus.value)
  params.set('limit', String(PAGE_SIZE))
  params.set('offset', String(offset))
  return params.toString()
}

const loadQueue = async () => {
  queueLoading.value = true
  queueOffset.value = 0
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/processing-queue?${queueParams(0)}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      queueItems.value = []
      queueTotal.value = 0
      return
    }
    queueItems.value = data.items || []
    queueTotal.value = data.total ?? 0
    queueOffset.value = queueItems.value.length
  } finally {
    queueLoading.value = false
  }
}

const loadMoreQueue = async () => {
  if (queueOffset.value >= queueTotal.value) return
  queueLoadingMore.value = true
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/processing-queue?${queueParams(queueOffset.value)}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) return
    const list = data.items || []
    queueItems.value = [...queueItems.value, ...list]
    queueOffset.value += list.length
  } finally {
    queueLoadingMore.value = false
  }
}

function failuresParams (offset = 0) {
  const params = new URLSearchParams()
  if (failuresStatus.value !== 'all') params.set('status', failuresStatus.value)
  params.set('limit', String(PAGE_SIZE))
  params.set('offset', String(offset))
  return params.toString()
}

const loadFailures = async () => {
  failuresLoading.value = true
  failuresOffset.value = 0
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/processing-failures?${failuresParams(0)}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      failuresItems.value = []
      failuresTotal.value = 0
      return
    }
    failuresItems.value = data.items || []
    failuresTotal.value = data.total ?? 0
    failuresOffset.value = failuresItems.value.length
  } finally {
    failuresLoading.value = false
  }
}

const loadMoreFailures = async () => {
  if (failuresOffset.value >= failuresTotal.value) return
  failuresLoadingMore.value = true
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/processing-failures?${failuresParams(failuresOffset.value)}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) return
    const list = data.items || []
    failuresItems.value = [...failuresItems.value, ...list]
    failuresOffset.value += list.length
  } finally {
    failuresLoadingMore.value = false
  }
}

onMounted(() => {
  loadSummary()
  loadQueue()
  loadFailures()
})
</script>

<style scoped>
.manager-processing { padding: 0; }

.page-title { margin: 0 0 0.5rem 0; font-size: 1.5rem; color: #1e293b; }
.page-desc { margin: 0 0 1.5rem 0; color: #64748b; font-size: 0.9rem; }
.manager-section { margin-bottom: 2rem; }
.section-title { margin: 0 0 0.75rem 0; font-size: 1.1rem; color: #334155; }

.summary-loading { padding: 1rem; color: #64748b; }
.summary-cards { display: flex; flex-direction: column; gap: 1.5rem; }
.summary-group-title { margin: 0 0 0.5rem 0; font-size: 1rem; color: #475569; }
.summary-row { display: flex; flex-wrap: wrap; gap: 0.75rem; }
.summary-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  min-width: 100px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}
.summary-card.failed { border-color: #fecaca; background: #fef2f2; }
.summary-card.danger { border-color: #f87171; background: #fee2e2; }
.summary-label { display: block; font-size: 0.8rem; color: #64748b; margin-bottom: 0.25rem; }
.summary-value { font-size: 1.25rem; font-weight: 700; color: #1e293b; }
.summary-unit { font-size: 0.85rem; color: #64748b; margin-left: 0.25rem; }

.card { background: white; border-radius: 8px; padding: 1.25rem; box-shadow: 0 1px 3px rgba(0,0,0,0.08); }
.filters { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 1rem; margin-bottom: 1rem; }
.filter-item { display: flex; flex-direction: column; gap: 0.25rem; }
.filter-label { font-size: 0.85rem; color: #64748b; font-weight: 500; }
.form-select { padding: 0.4rem 0.6rem; border: 1px solid #cbd5e1; border-radius: 6px; font-size: 0.9rem; }
.toolbar { display: flex; align-items: center; gap: 1rem; margin-bottom: 1rem; }
.total-note { font-size: 0.9rem; color: #64748b; }
.btn { padding: 0.4rem 0.9rem; border-radius: 6px; font-size: 0.9rem; cursor: pointer; border: 1px solid transparent; }
.btn-primary { background: #3b82f6; color: white; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secondary { background: #e2e8f0; color: #334155; }
.btn-sm { padding: 0.25rem 0.5rem; font-size: 0.85rem; }
.loading-state, .empty-state { padding: 2rem; text-align: center; color: #64748b; }
.table-wrap { overflow-x: auto; }
.data-table { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
.data-table th, .data-table td { padding: 0.5rem 0.75rem; text-align: left; border-bottom: 1px solid #e2e8f0; }
.data-table th { background: #f8fafc; color: #475569; font-weight: 600; }
.id-cell { font-size: 0.8rem; background: #f1f5f9; padding: 0.2rem 0.4rem; border-radius: 4px; }
.error-cell { max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 0.85rem; color: #64748b; }
.status-badge { display: inline-block; padding: 0.2rem 0.5rem; border-radius: 4px; font-size: 0.85rem; }
.status-badge.pending { background: #fef3c7; color: #92400e; }
.status-badge.processing { background: #dbeafe; color: #1e40af; }
.status-badge.completed { background: #dcfce7; color: #166534; }
.status-badge.failed { background: #fee2e2; color: #991b1b; }
.status-badge.pending_retry { background: #fef3c7; color: #92400e; }
.status-badge.retrying { background: #dbeafe; color: #1e40af; }
.status-badge.permanent_failure { background: #fee2e2; color: #991b1b; }
.status-badge.completed { background: #dcfce7; color: #166534; }
.load-more { margin-top: 1rem; text-align: center; }
</style>
